import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Plus, Trash2, Loader2, ChevronDown } from 'lucide-react';
import {
  Button,
  Input,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  getPatientAllergies,
  carryForwardImportedNoKnownAllergies,
  createPatientAllergy,
  createUserDefinedPatientAllergy,
  declineImportedAllergyLine,
  deletePatientAllergy,
  getImportedAllergyProfile,
  searchAllergies,
  getUserDefinedAllergies,
  updatePatient,
} from '../services/api';
import { useToast } from '../hooks/useToastContext';
import { getConflictCode } from '../utils/apiErrorDetail';
import type {
  ImportedAllergyProfileResponse,
  PatientResponse,
  PatientAllergyResponse,
} from '../services/api/types.gen';
import ImportedAllergiesNotice from './imported/ImportedAllergiesNotice';
import PatientDataImporterTool from './imported/PatientDataImporterTool';
import { UnclaimedRecordMarker } from './imported/UnclaimedRecordMarker';
import {
  convertedLines,
  outstandingLines,
  rulingErrorMessage,
} from './imported/importedChartLines';
import { useCanReconcileImports } from './imported/useCanReconcileImports';
import {
  IMPORTED_ALLERGIES_FETCH_FAILED_COPY,
  IMPORTED_ALLERGY_POPOVER_COPY,
  IMPORTED_FETCH_TRANSIENT_TOAST,
  NKA_DISMISS_IMPORTED_REASON,
} from './imported/importedChartLineCopy';
import ImportedFetchErrorNotice from './imported/ImportedFetchErrorNotice';
import NkaGuardDialog, { type NkaGuardState } from './imported/NkaGuardDialog';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { formatDateInAgencyZone } from '../utils/dateUtils';

interface PatientAllergiesProps {
  patient: PatientResponse;
  /**
   * Called after the allergy set changes (add or delete). The parent uses this
   * to refresh its cached allergy count so the tab badge stays correct without
   * waiting for a tab switch (ROG-3195). Toggling NKA is excluded — it only
   * applies when the list is already empty, so it never moves the count.
   */
  onAllergiesChanged?: () => void;
  /**
   * Called after this component writes to the *patient* row rather than to the
   * allergy list. Carrying an imported "no known allergies" assertion forward
   * sets `patients.no_known_allergies`, and the NKA checkbox reads that field
   * through the `patient` prop — so without this the box stays unchecked until
   * the page is reloaded, contradicting the banner directly above it.
   */
  onPatientChanged?: () => void;
}

interface AllergyFormData {
  allergy_name: string;
  reaction: string;
}

interface AllergyOption {
  // `id` is the option's identity: an FDB allergen id for "From list" results,
  // or a local catalog UUID for user-defined ("Not in list") options.
  id: string;
  name: string;
}

const PatientAllergies: React.FC<PatientAllergiesProps> = ({
  patient,
  onAllergiesChanged,
  onPatientChanged,
}) => {
  const { success, error: showError } = useToast();
  const [patientAllergies, setPatientAllergies] = useState<
    PatientAllergyResponse[]
  >([]);
  const [allergyOptions, setAllergyOptions] = useState<AllergyOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<AllergyOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [allergySearchError, setAllergySearchError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Dialog-only submit failures. Kept separate from `error` (list-area load/
  // delete failures): sharing one state let opening the dialog wipe a
  // "Failed to load allergies" warning, making a failed load read as
  // "No allergies on record".
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [nkaUpdating, setNkaUpdating] = useState(false);
  const [importedFetchFailed, setImportedFetchFailed] = useState(false);
  // The NKA guard (2026-08-28 ruling): which gate is open, which entrance
  // asked, and whether the dismiss-and-assert flow is mid-flight.
  const [nkaGuard, setNkaGuard] = useState<NkaGuardState>('closed');
  const [nkaGuardAction, setNkaGuardAction] = useState<'manual' | 'carry'>(
    'manual'
  );
  const [nkaGuardWorking, setNkaGuardWorking] = useState(false);
  const [isCarrying, setIsCarrying] = useState(false);
  const [nkaChecked, setNkaChecked] = useState(
    patient.no_known_allergies ?? false
  );
  const [addExternalMode, setAddExternalMode] = useState(false);
  const [externalAllergyOptions, setExternalAllergyOptions] = useState<
    AllergyOption[]
  >([]);
  const [filteredExternalOptions, setFilteredExternalOptions] = useState<
    AllergyOption[]
  >([]);
  const [loadingExternalOptions, setLoadingExternalOptions] = useState(true);
  const [selectedAllergyId, setSelectedAllergyId] = useState<string | null>(
    null
  );
  const [selectedExternalAllergyId, setSelectedExternalAllergyId] = useState<
    string | null
  >(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const allergySearchRequestRef = useRef(0);

  // Raw Kinnser allergy lines and the screen that rules on them (EMR-1369).
  const [importedProfile, setImportedProfile] =
    useState<ImportedAllergyProfileResponse | null>(null);
  const [importerOpen, setImporterOpen] = useState(false);
  // An imported chart still charted in the External EMR takes no allergy
  // writes from Roger (Take Over v2); the page banner says why.
  const chartLocked = patient.chart_locked === true;
  const canReconcileImports = useCanReconcileImports() && !chartLocked;
  const outstandingImportedAllergyLines = outstandingLines(
    importedProfile?.lines
  );
  // Coded by the import on a certain match (EMR-2415); revertible in the tool.
  const convertedImportedAllergyLines = convertedLines(importedProfile?.lines);

  const hasAllergies = patientAllergies.length > 0;

  useEffect(() => {
    setNkaChecked(patient.no_known_allergies ?? false);
  }, [patient.no_known_allergies]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
  } = useForm<AllergyFormData>({
    defaultValues: {
      allergy_name: '',
      reaction: '',
    },
  });

  const allergyNameValue = watch('allergy_name');

  const fetchUserDefinedAllergyOptions = async () => {
    try {
      setLoadingExternalOptions(true);
      const response = await getUserDefinedAllergies({});
      if (response.error || !response.data) {
        console.error(
          'Error fetching user-defined allergy options:',
          response.error
        );
      } else {
        const options = response.data.map(allergy => ({
          id: allergy.id,
          name: allergy.name,
        }));
        setExternalAllergyOptions(options);
        setFilteredExternalOptions(options);
      }
    } catch (err) {
      console.error('Error fetching user-defined allergy options:', err);
    } finally {
      setLoadingExternalOptions(false);
    }
  };

  /**
   * The raw Kinnser allergy profile.
   *
   * A failure must not blank the coded allergy list — that is what
   * drug-allergy screening reads — but it must not be silent either
   * (2026-08-28 ruling): a quiet failure hides the imported section, so a
   * patient with unreconciled lines reads identically to one with none. An
   * error strip takes the section's place; a connection-class failure also
   * toasts, because there a retry is likely to work.
   */
  const fetchImportedAllergies = async () => {
    const response = await getImportedAllergyProfile({
      path: { patient_id: patient.id },
    });
    // The generated client does not throw on HTTP errors.
    if (response.error || !response.data) {
      console.error('Error fetching imported allergies:', response.error);
      setImportedProfile(null);
      setImportedFetchFailed(true);
      const status = getHttpStatus(response) ?? getHttpStatus(response.error);
      if (status === undefined) {
        showError(IMPORTED_FETCH_TRANSIENT_TOAST);
      }
      return;
    }
    setImportedFetchFailed(false);
    setImportedProfile(response.data);
  };

  useEffect(() => {
    fetchAllergies();
    fetchUserDefinedAllergyOptions();
    void fetchImportedAllergies();
  }, [patient.id]);

  const fetchAllergies = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPatientAllergies({
        path: { patient_id: patient.id },
      });
      // The client is not configured with `throwOnError`, so a failed load
      // resolves with `error` set and no `data`. Without this branch the
      // list renders "No allergies on record" for a patient whose allergies
      // simply failed to load.
      if (response.error || !response.data) {
        console.error('Error fetching patient allergies:', response.error);
        setError('Failed to load allergies');
        return;
      }
      setPatientAllergies(response.data);
    } catch (err) {
      console.error('Error fetching patient allergies:', err);
      setError('Failed to load allergies');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllergyOptions = async (searchTerm: string) => {
    // FDB allergen search only returns results for terms of 3+ chars; skip the
    // request otherwise and fall back to the empty/prompt state.
    if (searchTerm.length < 3) {
      allergySearchRequestRef.current++;
      setAllergyOptions([]);
      setFilteredOptions([]);
      setAllergySearchError(false);
      setLoadingOptions(false);
      return;
    }

    const requestId = ++allergySearchRequestRef.current;

    try {
      setLoadingOptions(true);
      const response = await searchAllergies({
        query: { limit: 50, search: searchTerm },
      });

      if (requestId !== allergySearchRequestRef.current) {
        return;
      }

      // The client is not configured with `throwOnError`, so HTTP failures
      // (incl. the external FDB proxy) resolve with a populated `error` rather
      // than throwing. Handle them explicitly so stale results don't linger.
      if (response.error || !response.data) {
        console.error('Error fetching allergy options:', response.error);
        setAllergySearchError(true);
        setAllergyOptions([]);
        setFilteredOptions([]);
        return;
      }

      const options = response.data.items.map(item => ({
        id: item.fdb_allergen_id,
        name: item.name,
      }));
      setAllergySearchError(false);
      setAllergyOptions(options);
      setFilteredOptions(options);
    } catch (err) {
      if (requestId !== allergySearchRequestRef.current) {
        return;
      }
      console.error('Error fetching allergy options:', err);
      setAllergySearchError(true);
      setAllergyOptions([]);
      setFilteredOptions([]);
    } finally {
      if (requestId === allergySearchRequestRef.current) {
        setLoadingOptions(false);
      }
    }
  };

  // Search standard allergies from the API as the user types.
  useEffect(() => {
    if (!isDialogOpen || addExternalMode) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void fetchAllergyOptions(allergyNameValue.trim());
      setHighlightedIndex(0);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [allergyNameValue, addExternalMode, isDialogOpen]);

  // Filter user-defined options locally.
  useEffect(() => {
    if (!allergyNameValue) {
      setFilteredExternalOptions(externalAllergyOptions);
      setHighlightedIndex(0);
      return;
    }

    const query = allergyNameValue.toLowerCase();
    const filtered = externalAllergyOptions.filter(option =>
      option.name.toLowerCase().includes(query)
    );
    setFilteredExternalOptions(filtered);
    setHighlightedIndex(0);
  }, [allergyNameValue, externalAllergyOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDialog = () => {
    reset();
    setSubmitError(null);
    setAddExternalMode(false);
    setSelectedAllergyId(null);
    setSelectedExternalAllergyId(null);
    setAllergyOptions([]);
    setFilteredOptions([]);
    setAllergySearchError(false);
    setShowDropdown(false);
    setIsDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && !submitting) {
      setIsDialogOpen(false);
      reset();
      setSubmitError(null);
      setAddExternalMode(false);
      setSelectedAllergyId(null);
      setSelectedExternalAllergyId(null);
      setShowDropdown(false);
    }
  };

  const onSubmit = async (data: AllergyFormData) => {
    if (!data.allergy_name || !data.reaction) return;

    try {
      setSubmitting(true);
      setSubmitError(null);

      // Only `.error` is needed here. The generated client's throwOnError
      // overloads make `ReturnType<>` resolve to the throwing variant (no
      // `.error`), so type the shared handle structurally.
      let response: { error?: unknown };

      if (addExternalMode) {
        if (selectedExternalAllergyId) {
          response = await createPatientAllergy({
            path: { patient_id: patient.id },
            body: {
              allergy_id: selectedExternalAllergyId,
              reaction: data.reaction,
            },
          });
        } else {
          response = await createUserDefinedPatientAllergy({
            path: { patient_id: patient.id },
            body: {
              allergy_name: data.allergy_name,
              reaction: data.reaction,
            },
          });
        }
      } else {
        const selectedAllergy =
          allergyOptions.find(a => a.id === selectedAllergyId) ??
          allergyOptions.find(
            a => a.name.toLowerCase() === data.allergy_name.toLowerCase()
          );

        if (!selectedAllergy) {
          setSubmitError(
            'Please select an allergy from the list or use "Not in list" mode'
          );
          return;
        }

        // From-list options are FDB-proxied; the server materializes a local
        // catalog row from the FDB id + name on select.
        response = await createPatientAllergy({
          path: { patient_id: patient.id },
          body: {
            fdb_allergen_id: selectedAllergy.id,
            fdb_allergen_name: selectedAllergy.name,
            reaction: data.reaction,
          },
        });
      }

      // Client has no throwOnError; a failed create returns { error }. Surface the
      // error and keep the dialog open instead of a false success (allergy data is
      // safety-relevant), and don't fire the count refresh.
      if (response.error) {
        // 409 bodies carry a stable machine-readable `code` (ROG-2506 wire
        // contract) — key copy off it, never off the raw detail string.
        setSubmitError(
          getConflictCode(response.error) === 'PATIENT_ALLERGY_EXISTS'
            ? "This allergy is already on this patient's chart. To change the reaction, remove the entry and add it again."
            : 'Failed to add allergy'
        );
        return;
      }

      reset();
      setSelectedAllergyId(null);
      setSelectedExternalAllergyId(null);
      setIsDialogOpen(false);
      await fetchAllergies();
      onAllergiesChanged?.();
    } catch (err) {
      console.error('Error creating allergy:', err);
      setSubmitError('Failed to add allergy');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (patientAllergyId: string, allergyName: string) => {
    try {
      setDeletingId(patientAllergyId);
      setError(null);
      const response = await deletePatientAllergy({
        path: { patient_allergy_id: patientAllergyId },
      });
      // Client has no throwOnError; a failed delete returns { error }. Don't show
      // a success toast or fire the count refresh on failure.
      if (response.error) {
        setError('Failed to delete allergy');
        return;
      }
      success(`Successfully deleted patient allergy: ${allergyName}`);
      await fetchAllergies();
      onAllergiesChanged?.();
    } catch (err) {
      console.error('Error deleting allergy:', err);
      setError('Failed to delete allergy');
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * The plain patient-field write, reached only through the guard below.
   * Resolves true only when the patient row actually changed.
   */
  const writeManualNka = async (value: boolean): Promise<boolean> => {
    try {
      setNkaUpdating(true);
      setError(null);
      // The generated client does not throw on HTTP errors: a refused write
      // comes back as { error }, and must not read as the box flipping.
      const response = await updatePatient({
        path: { patient_id: patient.id },
        body: { no_known_allergies: value },
      });
      if (response.error) {
        console.error('Error updating no known allergies:', response.error);
        setError('Failed to update no known allergies');
        return false;
      }
      setNkaChecked(value);
      success(
        value ? 'Marked as no known allergies' : 'Cleared no known allergies'
      );
      onPatientChanged?.();
      return true;
    } catch (err) {
      console.error('Error updating no known allergies:', err);
      setError('Failed to update no known allergies');
      return false;
    } finally {
      setNkaUpdating(false);
    }
  };

  /**
   * Kinnser's NKA assertion accepted onto the chart, via the same guard.
   * Resolves true only when the chart now records NKA.
   */
  const writeCarriedNka = async (): Promise<boolean> => {
    setIsCarrying(true);
    try {
      // The generated client does not throw on HTTP errors.
      const response = await carryForwardImportedNoKnownAllergies({
        path: { patient_id: patient.id },
      });
      if (response.error) {
        showError(
          rulingErrorMessage(
            response.error,
            'Unable to carry this forward. Please try again.'
          )
        );
        return false;
      }
      success('No known allergies recorded on this chart.');
      void fetchAllergies();
      void fetchImportedAllergies();
      onAllergiesChanged?.();
      // This writes the patient's NKA field, which the checkbox reads —
      // refresh in the same breath so the banner and the box agree.
      onPatientChanged?.();
      return true;
    } finally {
      setIsCarrying(false);
    }
  };

  const executeNka = async (action: 'manual' | 'carry'): Promise<boolean> =>
    action === 'carry' ? writeCarriedNka() : writeManualNka(true);

  /**
   * The gate in front of asserting NKA, both entrances (2026-08-28 ruling).
   *
   * Coded allergies contradict the assertion and are clinician-entered, so
   * that state only explains — removing them is its own deliberate act.
   * Outstanding imported lines also contradict it, but they are the
   * importer's rows, so the clinician may settle both in one act: the modal
   * lists what gets dismissed and the confirm verb says so. A clean chart
   * asserts directly.
   */
  const requestNka = (action: 'manual' | 'carry') => {
    if (hasAllergies) {
      setNkaGuard('coded-blocked');
      return;
    }
    if (outstandingImportedAllergyLines.length > 0) {
      setNkaGuardAction(action);
      setNkaGuard('dismiss-imported');
      return;
    }
    void executeNka(action);
  };

  /**
   * [Accept and dismiss imported]: decline every outstanding line, then
   * assert. Declines run first so a failure leaves the chart consistent —
   * lines partially dismissed but NKA not yet asserted, and the retry meets
   * the remainder. Each decline stores the fixed reason; the lines stay on
   * the imported record for later review, they just stop being work.
   */
  const confirmDismissImportedAndAssert = async () => {
    setNkaGuardWorking(true);
    try {
      for (const line of outstandingImportedAllergyLines) {
        const response = await declineImportedAllergyLine({
          path: { patient_id: patient.id, line_id: line.id },
          body: { reason: NKA_DISMISS_IMPORTED_REASON },
        });
        if (response.error) {
          showError(
            rulingErrorMessage(
              response.error,
              'Unable to dismiss the imported allergy records. Please try again.'
            )
          );
          void fetchImportedAllergies();
          return;
        }
      }
      // The declines have landed either way; only a recorded assertion earns
      // the close. A failed assertion leaves the modal up with its error, and
      // the retry meets a chart with nothing left to dismiss.
      const asserted = await executeNka(nkaGuardAction);
      void fetchImportedAllergies();
      if (asserted) {
        setNkaGuard('closed');
      }
    } finally {
      setNkaGuardWorking(false);
    }
  };

  const onNkaChange = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      requestNka('manual');
    } else {
      void writeManualNka(false);
    }
  };

  // Allergen combobox used inside the dialog
  const renderAllergenInput = () => {
    const options = addExternalMode ? filteredExternalOptions : filteredOptions;
    const isOptionsLoading = addExternalMode
      ? loadingExternalOptions
      : loadingOptions;

    return (
      <div className='relative' ref={dropdownRef}>
        <Controller
          name='allergy_name'
          control={control}
          rules={{ required: 'Allergen is required' }}
          render={({ field }) => (
            <>
              <div className='relative'>
                <Input
                  {...field}
                  id='allergy_name'
                  placeholder='Type or select allergen...'
                  disabled={submitting || (addExternalMode && isOptionsLoading)}
                  inputSize='default'
                  onChange={e => {
                    field.onChange(e);
                    if (addExternalMode) {
                      setSelectedExternalAllergyId(null);
                    } else {
                      setSelectedAllergyId(null);
                    }
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={e => {
                    if (
                      e.key === 'Enter' &&
                      options.length > 0 &&
                      showDropdown
                    ) {
                      e.preventDefault();
                      const selected = options[highlightedIndex];
                      setValue('allergy_name', selected.name);
                      if (addExternalMode) {
                        setSelectedExternalAllergyId(selected.id);
                      } else {
                        setSelectedAllergyId(selected.id);
                      }
                      setShowDropdown(false);
                    } else if (e.key === 'Escape') {
                      setShowDropdown(false);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      setShowDropdown(true);
                      setHighlightedIndex(prev =>
                        prev < options.length - 1 ? prev + 1 : prev
                      );
                    } else if (e.key === 'ArrowUp') {
                      e.preventDefault();
                      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
                    }
                  }}
                />
                <ChevronDown className='absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none' />
              </div>
              {showDropdown &&
                !addExternalMode &&
                allergySearchError &&
                options.length === 0 && (
                  <div className='absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md px-3 py-2 text-sm text-muted-foreground'>
                    Search failed. Please try again.
                  </div>
                )}
              {showDropdown && options.length > 0 && (
                <div className='absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-auto'>
                  {options.map((option, index) => (
                    <button
                      key={option.id}
                      type='button'
                      className={`w-full px-3 py-1.5 text-left text-sm cursor-pointer ${
                        index === highlightedIndex
                          ? 'bg-accent text-accent-foreground'
                          : 'hover:bg-accent/50'
                      }`}
                      onClick={() => {
                        setValue('allergy_name', option.name);
                        if (addExternalMode) {
                          setSelectedExternalAllergyId(option.id);
                        } else {
                          setSelectedAllergyId(option.id);
                        }
                        setShowDropdown(false);
                      }}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        />
      </div>
    );
  };

  if (importerOpen) {
    // Replaces the list rather than floating over it, so Kinnser's wording
    // stays readable beside the entry the clinician is making from it.
    return (
      <PatientDataImporterTool
        patientId={patient.id}
        domain='allergy'
        lines={outstandingImportedAllergyLines}
        convertedLines={convertedImportedAllergyLines}
        codedRecordNames={Object.fromEntries(
          patientAllergies.map(allergy => [
            allergy.id,
            allergy.allergy?.name ?? '',
          ])
        )}
        onClose={() => setImporterOpen(false)}
        onChanged={() => {
          void fetchAllergies();
          void fetchImportedAllergies();
          onAllergiesChanged?.();
        }}
      />
    );
  }

  return (
    <div>
      {importedFetchFailed && (
        <ImportedFetchErrorNotice
          copy={IMPORTED_ALLERGIES_FETCH_FAILED_COPY}
          onRetry={() => void fetchImportedAllergies()}
        />
      )}
      <ImportedAllergiesNotice
        profile={importedProfile}
        outstandingCount={outstandingImportedAllergyLines.length}
        convertedCount={convertedImportedAllergyLines.length}
        canReconcile={canReconcileImports}
        onOpenImporter={
          canReconcileImports ? () => setImporterOpen(true) : undefined
        }
        onCarryForward={() => requestNka('carry')}
        isCarrying={isCarrying}
      />

      <NkaGuardDialog
        state={nkaGuard}
        lines={outstandingImportedAllergyLines}
        isWorking={nkaGuardWorking}
        onConfirmDismiss={() => void confirmDismissImportedAndAssert()}
        onClose={() => setNkaGuard('closed')}
      />

      {/* Top bar: NKA checkbox + Add button */}
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          {/* Enabled even with allergies or imported lines present: checking
              opens the guard dialog, which explains instead of a dead
              control that never says why (2026-08-28 ruling). */}
          <Checkbox
            id='nka'
            checked={nkaChecked}
            disabled={nkaUpdating || chartLocked}
            onCheckedChange={onNkaChange}
            aria-label='NKA (Food / Drug / Latex / Environmental)'
          />
          <label
            htmlFor='nka'
            className='text-sm font-medium leading-none cursor-pointer select-none'
          >
            NKA (No Known Allergies)
          </label>
        </div>
        {!nkaChecked && !chartLocked && (
          <Button size='sm' onClick={openDialog}>
            <Plus className='h-3.5 w-3.5 mr-1.5' />
            Add Allergy
          </Button>
        )}
      </div>

      {/* Allergy list */}
      {loading ? (
        <div className='flex items-center justify-center py-8'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : error ? (
        <div className='text-center py-8 text-sm text-muted-foreground'>
          {error}
        </div>
      ) : patientAllergies.length === 0 &&
        outstandingImportedAllergyLines.length === 0 ? (
        // Roger's own NKA and a plain empty list are different facts, and both
        // are different again from Kinnser's imported assertion above.
        <div className='text-center py-8 text-sm text-muted-foreground'>
          {nkaChecked
            ? 'Patient has no known allergies'
            : 'No allergies on record'}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Allergen</TableHead>
              <TableHead>Reaction</TableHead>
              <TableHead className='w-[60px]' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {patientAllergies.map(allergy => (
              <TableRow key={allergy.id}>
                <TableCell className='font-medium'>
                  {allergy.allergy?.name || 'Unknown'}
                </TableCell>
                <TableCell>{allergy.reaction}</TableCell>
                <TableCell>
                  <Button
                    variant='ghost'
                    size='sm'
                    disabled={deletingId === allergy.id || chartLocked}
                    onClick={() =>
                      onDelete(allergy.id, allergy.allergy?.name || 'Unknown')
                    }
                  >
                    {deletingId === allergy.id ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      <Trash2 className='h-4 w-4 text-destructive' />
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {/* Imported lines sit after the coded rows: Kinnser's own ordering
                is not carried in the contract, so any position among them would
                imply a sequence nobody reported. */}
            {outstandingImportedAllergyLines.map(line => (
              <TableRow key={line.id} className='bg-amber-50/40'>
                <TableCell>
                  <UnclaimedRecordMarker
                    popoverLabel='Why this allergy is marked as imported'
                    lines={[IMPORTED_ALLERGY_POPOVER_COPY]}
                    testId={`imported-allergy-${line.id}`}
                  >
                    <span className='font-medium'>
                      {line.allergen || 'Unnamed allergen'}
                    </span>
                  </UnclaimedRecordMarker>
                  <div
                    className='text-xs text-muted-foreground mt-1'
                    data-testid={`imported-allergy-${line.id}-imported-at`}
                  >
                    Imported {formatDateInAgencyZone(line.imported_at)}
                  </div>
                </TableCell>
                <TableCell>{line.reaction}</TableCell>
                {/* No delete: the only two things a clinician may do to an
                    imported line happen in the importer tool. */}
                <TableCell />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Add Allergy Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Add Allergy</DialogTitle>
            <DialogDescription>
              {addExternalMode
                ? 'Add an allergy not found in the standard list.'
                : 'Search for an allergen and enter the reaction.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4 py-2'>
            {/* Mode toggle */}
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={() => {
                  setAddExternalMode(false);
                  setSelectedAllergyId(null);
                  setSelectedExternalAllergyId(null);
                  setAllergySearchError(false);
                  reset();
                }}
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  !addExternalMode
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                From list
              </button>
              <button
                type='button'
                onClick={() => {
                  setAddExternalMode(true);
                  setSelectedAllergyId(null);
                  setSelectedExternalAllergyId(null);
                  reset();
                }}
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  addExternalMode
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Not in list
              </button>
            </div>

            {addExternalMode && (
              <p className='text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2'>
                Make sure to check this allergy is not in our system first.
              </p>
            )}

            {/* Allergen field */}
            <div>
              <Label className='text-xs text-muted-foreground mb-1 block'>
                Allergen
              </Label>
              {renderAllergenInput()}
              {errors.allergy_name && (
                <p className='text-xs text-destructive mt-1'>
                  {errors.allergy_name.message}
                </p>
              )}
            </div>

            {/* Reaction field */}
            <div>
              <Label className='text-xs text-muted-foreground mb-1 block'>
                Reaction
              </Label>
              <Input
                id='reaction'
                placeholder='Enter reaction...'
                disabled={submitting}
                inputSize='default'
                {...register('reaction', {
                  required: 'Reaction is required',
                })}
              />
              {errors.reaction && (
                <p className='text-xs text-destructive mt-1'>
                  {errors.reaction.message}
                </p>
              )}
            </div>

            {/* Submit failures must be visible here — the list-area error
                message sits behind the modal overlay. */}
            {submitError && (
              <p className='text-sm text-destructive'>{submitError}</p>
            )}

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleDialogClose(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={submitting || (!addExternalMode && loadingOptions)}
              >
                {submitting && (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                )}
                Add Allergy
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientAllergies;
